import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, X } from "lucide-react";

// Defensive date normalizer — covers Excel serial dates and dd/mm/yyyy strings
// that may have been persisted from CSV imports.
const formatSampleDate = (raw) => {
  if (raw === null || raw === undefined) return '-';
  const s = String(raw).trim();
  if (!s) return '-';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const num = Number(s);
  if (Number.isFinite(num) && num >= 20000 && num <= 80000) {
    const ms = (num * 86400 * 1000) + Date.UTC(1899, 11, 30);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) { const [, a, b, y] = m; return `${y}-${b.padStart(2,'0')}-${a.padStart(2,'0')}`; }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

const ExistingContactSection = ({ 
  lead, 
  editData, 
  setEditData, 
  isEditing 
}) => {
  // Show this section for either:
  //   - Legacy "Existing contact" leads (carry the manual Previous Status flow), or
  //   - ANY lead that has order_history attached (bulk-import "Order exists" leads,
  //     CSV-uploaded order data, etc.). Previously we gated on data_source alone,
  //     which hid past orders from the AM whenever the source was anything other
  //     than the literal string "Existing contact".
  const hasOrders = Array.isArray(lead.order_history) && lead.order_history.length > 0;
  const isExistingContact = lead.data_source === 'Existing contact';
  if (!isExistingContact && !hasOrders) {
    return null;
  }

  return (
    <div className="pt-4 border-t border-slate-200">
      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">Existing Contact</span>
        Customer History
      </h4>
      <div className="space-y-4">
        {/* Previous Status (Query / Order) — only meaningful for legacy "Existing contact" leads */}
        {isExistingContact && (
          <div className="space-y-2 max-w-xs">
            <Label className="text-slate-500 text-sm">Previous Status</Label>
            {isEditing ? (
              <Select 
                value={editData.previous_status || ""} 
                onValueChange={(v) => setEditData(prev => ({ 
                  ...prev, 
                  previous_status: v,
                  order_history: v === 'Query' ? [] : (prev.order_history || [])
                }))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Query">Query</SelectItem>
                  <SelectItem value="Order">Order</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className={`text-sm font-medium ${lead.previous_status === 'Order' ? 'text-green-600' : 'text-slate-900'}`}>
                {lead.previous_status || "-"}
              </p>
            )}
          </div>
        )}
        
        {/* Order History Table — render whenever there's order data OR the user
            has flagged previous_status = Order on an Existing-contact lead. */}
        {(hasOrders ||
          editData.previous_status === 'Order' ||
          lead.previous_status === 'Order') && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-600 text-sm font-medium">Order History</Label>
              {isEditing && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const newOrder = {
                      id: Date.now().toString(),
                      lead_id: '',
                      fabric_type: '',
                      composition: '',
                      order_date: ''
                    };
                    setEditData(prev => ({
                      ...prev,
                      order_history: [...(prev.order_history || []), newOrder]
                    }));
                  }}
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Order
                </Button>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Lead ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Fabric Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Composition</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Order Date</th>
                    {isEditing && <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {isEditing ? (
                    (editData.order_history || []).length > 0 ? (
                      editData.order_history.map((order, idx) => (
                        <tr key={order.id || idx} className="bg-white">
                          <td className="px-3 py-2">
                            <Input
                              value={order.lead_id || ''}
                              onChange={(e) => {
                                const updated = [...(editData.order_history || [])];
                                updated[idx] = { ...updated[idx], lead_id: e.target.value };
                                setEditData(prev => ({ ...prev, order_history: updated }));
                              }}
                              className="h-8 text-xs"
                              placeholder="LF-001"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Select
                              value={order.fabric_type || ''}
                              onValueChange={(v) => {
                                const updated = [...(editData.order_history || [])];
                                updated[idx] = { ...updated[idx], fabric_type: v };
                                setEditData(prev => ({ ...prev, order_history: updated }));
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Greige">Greige</SelectItem>
                                <SelectItem value="Dyed">Dyed</SelectItem>
                                <SelectItem value="Printed">Printed</SelectItem>
                                <SelectItem value="Denim">Denim</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={order.composition || ''}
                              onChange={(e) => {
                                const updated = [...(editData.order_history || [])];
                                updated[idx] = { ...updated[idx], composition: e.target.value };
                                setEditData(prev => ({ ...prev, order_history: updated }));
                              }}
                              className="h-8 text-xs"
                              placeholder="100% Cotton"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="date"
                              value={order.order_date || ''}
                              onChange={(e) => {
                                const updated = [...(editData.order_history || [])];
                                updated[idx] = { ...updated[idx], order_date: e.target.value };
                                setEditData(prev => ({ ...prev, order_history: updated }));
                              }}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const updated = editData.order_history.filter((_, i) => i !== idx);
                                setEditData(prev => ({ ...prev, order_history: updated }));
                              }}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-slate-400 text-xs">
                          No orders added. Click "Add Order" to add order history.
                        </td>
                      </tr>
                    )
                  ) : (
                    (lead.order_history || []).length > 0 ? (
                      lead.order_history.map((order, idx) => (
                        <tr key={order.id || idx} className="bg-white">
                          <td className="px-3 py-2 text-slate-900">{order.lead_id || '-'}</td>
                          <td className="px-3 py-2 text-slate-900">{order.fabric_type || '-'}</td>
                          <td className="px-3 py-2 text-slate-900">{order.composition || '-'}</td>
                          <td className="px-3 py-2 text-slate-900">{formatSampleDate(order.order_date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-center text-slate-400 text-xs">
                          No order history available
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Majority Business Share - Only if fabric_type is "Greige" */}
        {(editData.fabric_type === 'Greige' || lead.fabric_type === 'Greige') && (
          <div className="space-y-2 max-w-xs">
            <Label className="text-slate-500 text-sm">Majority Business Share</Label>
            {isEditing ? (
              <Select 
                value={editData.majority_business_share || ""} 
                onValueChange={(v) => setEditData(prev => ({ ...prev, majority_business_share: v }))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rayon">Rayon</SelectItem>
                  <SelectItem value="Cotton">Cotton</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-slate-900 text-sm">{lead.majority_business_share || "-"}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExistingContactSection;
